import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getBulkQuoteData } from "store/priceQuote/actionCreator";
import quoteImg from "svg/quote.svg";
import toast_error from "svg/toast_error.svg";
import { toast } from "react-toastify";
import info from "svg/info.svg";
import Modal from "common/modal";
import axios from "services/config";
import groupBy from "lodash/groupBy";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import maxBy from "lodash/maxBy";
import sortBy from "lodash/sortBy";
import { storeValue, currencyValue } from "../utils";
import CustomTooltip from "common/tooltip";
import Button from "react-bootstrap/Button";
import { Row, Col, Card, Spinner } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { api } from "../../../store/slices/api";
import { isEmpty } from "lodash";

const UpdateLaborPricing = () => {
  const normalizeRate = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : "0.00";
  };
  const { quoteId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [successModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [defaultBillRate, setDefaultBillRate] = useState({});
  const [selectedLaborItem, setSelectedLaborItem] = useState([]);
  const [exceptionBillRate, setExceptionBillRate] = useState({});
  const [isDefaultRateLoaded, setIsDefaultRateLoaded] = useState(false);
  const [isExceptionRateLoaded, setIsExceptionRateLoaded] = useState(false);
  const [laborData, setLaborData] = useState([]);
  const [installerRateInputs, setInstallerRateInputs] = useState({});
  const { sub } = useSelector((state) => state.loggedInUser);
  const { data: quote = {} } = api.useGetPriceQuoteQuery(quoteId);
  const [updateLaborPricing] = api.useDeprecatedUpdateLaborPricingMutation();
  const [createLaborPricing] = api.useDeprecatedCreateLaborPricingMutation();

  const loadPrices = async () => {
    const { storeNumber, accountNumber, jobNumber, prcNumber } = quote;
    const { laborCodesList } = await dispatch(
      getBulkQuoteData({ quoteId, acctNbr: accountNumber, prcNbr: prcNumber, jobNumber }),
    );
    const groupedLaborItems = groupBy(laborCodesList, "laborCode");
    const laborData = map(groupedLaborItems, (items) => maxBy(items, "price"));
    setLaborData(laborData);
    let selectableIds = [];

    try {
      const [defaultResponse, exceptionResponse] = await Promise.all([
        axios.get("/fc-labor-table/laborTable/laborCodeSearch", {
          params: { storeNbr: storeNumber, isExceptionPrice: false },
        }),
        axios.get("/fc-labor-table/laborTable/laborCodeSearch", {
          params: { storeNbr: storeNumber, accountNumber, jobNumber, isExceptionPrice: true },
        }),
      ]);
      const exceptionBillRateData = keyBy(exceptionResponse.data, "laborCode");
      const defaultBillRateData = keyBy(defaultResponse.data, "laborCode");
      setDefaultBillRate(defaultBillRateData);
      setExceptionBillRate(exceptionBillRateData);

      const initialInstallerRates = {};
      laborData.forEach((item) => {
        const exceptionRate = exceptionBillRateData[item.laborCode]?.installerRate;
        const baseRate = defaultBillRateData[item.laborCode]?.standardInstallerRate;
        if (exceptionRate !== undefined && exceptionRate !== null && exceptionRate !== "") {
          initialInstallerRates[item.laborCodeId] = normalizeRate(exceptionRate);
        } else if (baseRate !== undefined && baseRate !== null && baseRate !== "") {
          initialInstallerRates[item.laborCodeId] = normalizeRate(baseRate);
        } else {
          initialInstallerRates[item.laborCodeId] = "0.00";
        }
      });
      setInstallerRateInputs(initialInstallerRates);

      selectableIds = laborData
        .filter((labor) => isLaborSelectable(labor, exceptionBillRateData))
        .map((item) => item.laborCodeId);
    } catch (err) {
      setDefaultBillRate({});
      setExceptionBillRate({});
    }
    setIsDefaultRateLoaded(true);
    setIsExceptionRateLoaded(true);
    setSelectedLaborItem(selectableIds);
  };

  useEffect(() => {
    if (!isEmpty(quote)) loadPrices();
  }, [quote]);

  const isLaborSelectable = (labor, exceptionRates = exceptionBillRate) => {
    const exceptionRate = normalizeRate(exceptionRates[labor.laborCode]?.exceptionPrice ?? 0);
    const laborPrice = normalizeRate(labor.price);
    return labor.laborCode && Number(laborPrice) !== 0 && laborPrice !== exceptionRate;
  };

  const sortedLaborData = sortBy(laborData, [(item) => !isLaborSelectable(item), "laborCode"]);

  const onLaborSelection = (laborCodeId) => {
    if (selectedLaborItem.includes(laborCodeId)) {
      setSelectedLaborItem(selectedLaborItem.filter((id) => id !== laborCodeId));
    } else {
      setSelectedLaborItem([...selectedLaborItem, laborCodeId]);
    }
  };

  const handleSelectAllLabor = () => {
    const allSelectableLaborIds = sortedLaborData
      .filter((labor) => isLaborSelectable(labor, exceptionBillRate))
      .map((item) => item.laborCodeId);

    const isAllSelected = allSelectableLaborIds.every((id) => selectedLaborItem.includes(id));
    setSelectedLaborItem(isAllSelected ? [] : allSelectableLaborIds);
  };

  const hasInstallerRateAlert = sortedLaborData
    .filter((item) => selectedLaborItem.includes(item.laborCodeId))
    .some((item) => {
      const installerRate = Number(normalizeRate(installerRateInputs[item.laborCodeId]));
      const billRate = Number(normalizeRate(item.price));
      return installerRate > billRate;
    });

  const getRelatedMsg = (data) => {
    if (!data.laborCode) {
      return "Labor code is missing";
    }
    if (normalizeRate(data.price) === "0.00") {
      return "Quote bill rate is $0.00";
    }
    if (normalizeRate(data.price) === normalizeRate(exceptionBillRate[data.laborCode]?.exceptionPrice ?? 0)) {
      return "Quote bill rate matches exception bill rate";
    }
    return "";
  };

  const handleSave = async () => {
    setIsSaving(true);
    const { storeNumber, accountNumber, jobNumber } = quote;
    const selectedItems = sortedLaborData.filter((item) => selectedLaborItem.includes(item.laborCodeId));

    const request = selectedItems.map((item) => {
      const existingException = exceptionBillRate[item.laborCode];
      const laborCodeId = existingException?.id ?? defaultBillRate[item.laborCode]?.id;
      const installerPayRate = normalizeRate(installerRateInputs[item.laborCodeId]);

      const payload = {
        accountNumber: accountNumber,
        jobNumber: !jobNumber || jobNumber.length > 3 ? "" : jobNumber,
        laborBillRate: normalizeRate(item.price),
        installerPayRate,
        laborCodeId: laborCodeId,
        storeNumber: storeNumber,
      };
      if (existingException) {
        updateLaborPricing({ ...payload, updatedBy: sub });
      } else {
        createLaborPricing({ ...payload, accountName: quote.accountName, jobName: quote.jobName, createdBy: sub });
      }
    });

    try {
      await toast.promise(Promise.all(request), {
        pending: "Updating labor pricing...",
        success: "Labor pricing updated successfully",
        error: "Failed to update labor pricing",
      });
      setShowConfirmModal(false);
      navigate(`/priceQuote/${quoteId}#quote-summary`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderMoney = (value, isLoaded, defaultValue = "$0.00") => {
    if (!isLoaded) return <Spinner className="me-3" animation="border" size="sm" />;
    if (value === undefined || value === null || value === "") return defaultValue;
    return currencyValue(value) === undefined || currencyValue(value) === null || currencyValue(value) === ""
      ? defaultValue
      : currencyValue(value);
  };

  const showAlert =
    isDefaultRateLoaded &&
    isExceptionRateLoaded &&
    laborData.length > 0 &&
    laborData.every(
      (item) =>
        !item.laborCode ||
        normalizeRate(item.price) === "0.00" ||
        normalizeRate(item.price) === normalizeRate(exceptionBillRate[item.laborCode]?.exceptionPrice ?? 0),
    );

  const alertMessage = showAlert
    ? "No labor codes are selectable"
    : hasInstallerRateAlert
      ? "Installer Pay Rate cannot be greater than Quote Bill Rate"
      : null;

  return (
    <>
      <div className="overflow-auto position-relative container-fluid search-form-container">
        <h1 className="search-form-container__title search-form-container__title--text">
          <img src={quoteImg} alt="quote" className="p-2 mb-1" />
          Update Labor Pricing
        </h1>

        <div className="mt-md-5 mb-5 overflow-auto justify-content-between ps-2">
          <Row>
            <Col
              xs={12}
              md={4}
              className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start"
            >
              <div className="info-item__label ">Account</div>
              <div className="info-item__value text-end text-md-start">
                {!quote?.accountNumber ? "N/A" : `${quote.accountNumber} - ${quote.accountName}`}
              </div>
            </Col>

            <Col
              xs={12}
              md={4}
              className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start"
            >
              <div className="info-item__label fw-bold">Job</div>
              <div className="info-item__value text-end text-md-start">
                {!quote?.jobNumber ? "N/A" : `${quote.jobNumber} : ${quote.jobName}`}
              </div>
            </Col>

            <Col
              xs={12}
              md={4}
              className="d-flex flex-row flex-md-column justify-content-between justify-content-md-start"
            >
              <div className="info-item__label fw-bold">Store</div>
              <div className="info-item__value text-end text-md-start">
                {storeValue(quote.storeNumber, quote.storeName)}
              </div>
            </Col>
          </Row>
        </div>
        <div className="prod-header prc-sizing">Labor Codes</div>
        <div className="d-flex justify-content-between ">
          {alertMessage ? (
            <div
              className="alert-msg alert-danger d-flex align-items-center"
              role="alert"
              style={{ minWidth: "150px" }}
            >
              <img src={toast_error} className="me-2" alt="error" />
              {alertMessage}
            </div>
          ) : (
            <div className="d-flex align-items-center ps-4">
              <div className="pe-3">
                <input
                  type="checkbox"
                  aria-label="Checkbox select all of rows"
                  className="checkbox-labor"
                  checked={sortedLaborData
                    .filter((item) => isLaborSelectable(item))
                    .every((item) => selectedLaborItem.includes(item.laborCodeId))}
                  onChange={handleSelectAllLabor}
                />
              </div>
              <div className="checkbox-prc-text-input text-nowrap">Select All</div>
            </div>
          )}

          <div className="d-flex justify-content-start gap-lg-5 ">
            <div
              className="d-flex justify-content-between align-items-center text-nowrap fs-4 fw-medium ps-lg-3"
              style={{ minWidth: "277px", paddingLeft: "2px" }}
            >
              Bill Rate
            </div>
            <div
              className="d-flex justify-content-between align-items-center text-nowrap fs-4 fw-medium"
              style={{ minWidth: "277px" }}
            >
              Installer Rate
            </div>
          </div>
        </div>
        {sortedLaborData?.map((data, index) => {
          const baseBillRate = defaultBillRate[data.laborCode]?.standardBillRate;
          const exceptionBillRateValue = exceptionBillRate[data.laborCode]?.exceptionPrice;
          const baseInstallerRate = defaultBillRate[data.laborCode]?.standardInstallerRate;
          const exceptionInstallerRate = exceptionBillRate[data.laborCode]?.installerRate;

          return (
            <Card className="bulkUpdateQuote_container" key={`${data.laborCodeId}-${index}`}>
              <Card.Body className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  {isLaborSelectable(data) ? (
                    <div className="pe-3 ps-2">
                      <input
                        type="checkbox"
                        className="checkbox-labor"
                        onChange={() => onLaborSelection(data.laborCodeId)}
                        checked={selectedLaborItem.includes(data.laborCodeId)}
                      />
                    </div>
                  ) : (
                    <CustomTooltip placement="top" trigger="hover" text={getRelatedMsg(data)}>
                      <img src={info} alt="info" className="infoIconLabor ms-2 me-3" />
                    </CustomTooltip>
                  )}
                  <div className="info-item__value prod-name text-break">
                    {(!data.laborCode ? "N/A" : data.laborCode) + " — " + (data.description ?? "N/A")}
                  </div>
                </div>
                <div className="d-flex justify-content-between gap-2 gap-md-3 gap-lg-5">
                  <div className="d-flex justify-content-between gap-3 align-items-center">
                    <div className="d-flex flex-column justify-content-start" style={{ minWidth: "70px" }}>
                      <div className="info-item__label">Store</div>
                      <div className="info-item__value">{renderMoney(baseBillRate, isDefaultRateLoaded)}</div>
                    </div>
                    <div className="d-flex flex-column" style={{ minWidth: "70px" }}>
                      <div className="info-item__label">Exception</div>
                      <div className="info-item__value">
                        {renderMoney(exceptionBillRateValue, isExceptionRateLoaded, "N/A")}
                      </div>
                    </div>
                    <div className="vr opacity-50 align-self-center" style={{ width: "1px", height: "60px" }}></div>

                    <div className="d-flex flex-column" style={{ minWidth: "70px" }}>
                      <div className="info-item__label">New</div>
                      <div className="info-item__value">{renderMoney(data.price ?? 0, isExceptionRateLoaded)}</div>
                    </div>
                  </div>
                  <div className="d-flex justify-content-start gap-3 align-items-center">
                    <div className="d-flex flex-column" style={{ minWidth: "70px" }}>
                      <div className="info-item__label">Store</div>
                      <div className="info-item__value">{renderMoney(baseInstallerRate, isDefaultRateLoaded)}</div>
                    </div>
                    <div className="d-flex flex-column" style={{ minWidth: "70px" }}>
                      <div className="info-item__label">Exception</div>
                      <div className="info-item__value">
                        {renderMoney(exceptionInstallerRate, isExceptionRateLoaded, "N/A")}
                      </div>
                    </div>
                    <div className="vr opacity-50 align-self-center" style={{ width: "1px", height: "60px" }}></div>
                    <div className="d-flex flex-column" style={{ minWidth: "45px" }}>
                      <div className="info-item__label mt-1">New</div>
                      <div className="info-item__value mt-1.5">
                        {!isDefaultRateLoaded || !isExceptionRateLoaded ? (
                          <div className="pe-5">
                            <Spinner animation="border" size="sm" />{" "}
                          </div>
                        ) : (
                          <input
                            className="form-control form-control-sm w-auto"
                            type="text"
                            value={
                              installerRateInputs[data.laborCodeId] === null ||
                              installerRateInputs[data.laborCodeId] === undefined ||
                              installerRateInputs[data.laborCodeId] === "null"
                                ? "0.00"
                                : String(installerRateInputs[data.laborCodeId])
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
                                setInstallerRateInputs((prev) => ({
                                  ...prev,
                                  [data.laborCodeId]: value,
                                }));
                              }
                            }}
                            onBlur={(e) => {
                              const value = normalizeRate(e.target.value);
                              setInstallerRateInputs((prev) => ({
                                ...prev,
                                [data.laborCodeId]: value,
                              }));
                            }}
                            disabled={!isLaborSelectable(data)}
                            style={{ minWidth: "50px", maxWidth: "70px" }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          );
        })}

        <div className="d-flex justify-content-between flex-column flex-md-row mt-10 footer-spacing">
          <Button variant="outline-primary" onClick={() => navigate(`/priceQuote/${quoteId}#quote-summary`)}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              if (!quote.accountNumber || !quote.jobNumber) {
                toast.error("Must have an account and Job on the quote to proceed");
              } else {
                setShowConfirmModal(true);
              }
            }}
            disabled={
              showAlert ||
              hasInstallerRateAlert ||
              selectedLaborItem.length === 0 ||
              !isDefaultRateLoaded ||
              !isExceptionRateLoaded
            }
          >
            Update Labor Pricing
          </Button>
        </div>
      </div>
      {showConfirmModal && (
        <Modal
          title={"Confirmation"}
          saveButtonText={"Confirm"}
          closeButtonText={"Cancel"}
          handleSave={handleSave}
          handleCancel={() => setShowConfirmModal(false)}
          isSaveBtnDisabled={isSaving}
          size={"lg"}
        >
          <>
            {sortedLaborData
              .filter((item) => selectedLaborItem.includes(item.laborCodeId))
              .some(
                (item) =>
                  Number(normalizeRate(item.price)) <
                  Number(normalizeRate(exceptionBillRate[item.laborCode]?.exceptionPrice ?? 0)),
              ) && (
              <div className="alert-msg alert-danger d-flex align-items-center mb-3" role="alert">
                <img src={toast_error} />
                &nbsp;<div>District leadership will be notified of all price decreases.</div>
              </div>
            )}
            <p className="confirmation-text">
              Are you sure you want to update labor pricing for the selected labor codes?
            </p>
          </>
        </Modal>
      )}
      {successModal && (
        <Modal title={"Success!"} closeButtonText={"Ok"} handleCancel={handleCancel}>
          <p className="confirmation-text">
            You have successfully updated labor pricing for Job {quote.jobNumber} : {quote.jobName}.
            <br />
          </p>
        </Modal>
      )}
    </>
  );
};
export default UpdateLaborPricing;
